/**
 * 引用回复（任务 14.6；纯函数，可单测）。协议口径＝旧版 im_msg_parser 实读：
 *
 * - 引用信息放在消息 **ext**（后端原样存储/推送，不认识该字段的客户端自然降级
 *   为普通文本），发送帧仍是文本 msgType——引用不是独立消息类型。
 * - 发送形态（buildReplyExt）：`{"reply":{"id","name","sender","text"}}`，
 *   name/sender 冗余双写（旧版同形）。21ah-2 起**多写两个字段**
 *   `msgType`+`srcMsg`（原消息类型 + 正文 envelope）：原消息找不到时接收端能把
 *   它原样渲染成真气泡（图片画出来、名片/文件/语音照常），而不是只剩「[图片]」
 *   这种半吊子占位。旧客户端只读自己认识的 key，多出来的字段会忽略；后端 ext
 *   原样存转——纯增量、向后兼容。老消息没这俩字段 → msgType=0，退回文本快照。
 * - 解析（parseReplyExt＝旧版 parseReply）：容器别名 reply|replyTo|顶层；
 *   id 别名 id|msgId|messageId（数字/数字串都收）；text 别名 text|content|msg；
 *   name 别名 name|nickname|sender。三者全空视为无引用（相册等其他 ext 形态
 *   天然落到这条返回 null）。
 * - 被引用消息的快照文本（replySnapshotText＝旧版 _replyPreviewText）：发送时
 *   定格进 ext，之后原消息被删/改不回溯。文本截 60 字；媒体类用占位标签——
 *   file/video 旧版函数没写（落到空串），新版补 [文件]/[媒体]（与 messagePreview
 *   同表），属刻意偏差。
 */

// 只 type 导入 messageModel：它反向 import 本模块的 parseReplyExt，别成值循环
import type { MessageContent } from './messageModel';
import {
  buildContactCardEnvelope,
  buildFileEnvelope,
  buildImageEnvelope,
  buildVoiceEnvelope,
} from './preview';
import { buildStickerEnvelope } from './stickerModel';
import { MsgType } from './msgTypes';

export interface ReplyRef {
  /** 被引用消息的服务端 id（0=未知：仅展示，点击跳转不可用） */
  readonly id: number;
  /** 被引用消息发送者昵称（可空，渲染兜底「原消息」） */
  readonly name: string;
  /** 快照文本（可空，渲染兜底「(空)」） */
  readonly text: string;
  /**
   * 原消息类型（21ah-2）。0=手上只有文本快照：老消息的 ext 没这字段，或原消息
   * 是文本（文本快照本身就够）/阅后即焚/端到端加密（**刻意不带正文**，见
   * replySnapshotRef）。
   */
  readonly msgType: number;
  /** 原消息正文 envelope（msgType>0 时有效，parseMessageContent 可解回真气泡） */
  readonly srcMsg: string;
}

function readInt(r: Record<string, unknown>, keys: readonly string[]): number {
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
    if (typeof v === 'string') {
      const n = Number.parseInt(v.trim(), 10);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

function readStr(r: Record<string, unknown>, keys: readonly string[]): string {
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return '';
}

/** ext → 引用信息。非 JSON / 无引用字段返回 null。 */
export function parseReplyExt(ext: string): ReplyRef | null {
  const body = ext.trim();
  if (body === '' || !body.startsWith('{')) return null;
  let decoded: unknown = null;
  try {
    decoded = JSON.parse(body);
  } catch {
    return null;
  }
  if (typeof decoded !== 'object' || decoded === null || Array.isArray(decoded)) return null;
  const outer = decoded as Record<string, unknown>;
  const container = outer['reply'] ?? outer['replyTo'] ?? outer;
  if (typeof container !== 'object' || container === null || Array.isArray(container)) return null;
  const r = container as Record<string, unknown>;
  const id = readInt(r, ['id', 'msgId', 'messageId']);
  const text = readStr(r, ['text', 'content', 'msg']);
  const name = readStr(r, ['name', 'nickname', 'sender']);
  if (id === 0 && text === '' && name === '') return null;
  // 21ah-2 增量字段（老消息没有=0/空）。键名避开 text 的别名表（content/msg）
  const msgType = readInt(r, ['msgType', 'msg_type', 'srcType']);
  const srcMsg = readStr(r, ['srcMsg', 'src_msg', 'sourceMsg']);
  return { id, name, text, msgType: srcMsg !== '' ? msgType : 0, srcMsg };
}

/**
 * 引用信息 → 发送帧 ext（旧版 buildReplyExt 同形，name/sender 双写）。
 * msgType/srcMsg 只在有正文快照时才写（21ah-2）——文本引用维持旧版字节同形。
 */
export function buildReplyExt(reply: ReplyRef): string {
  const body: Record<string, unknown> = {
    id: reply.id,
    name: reply.name,
    sender: reply.name,
    text: reply.text,
  };
  if (reply.msgType > 0 && reply.srcMsg !== '') {
    body['msgType'] = reply.msgType;
    body['srcMsg'] = reply.srcMsg;
  }
  return JSON.stringify({ reply: body });
}

const REPLY_TEXT_MAX = 60;

/** 被引用消息 → 快照文本（发送时定格进 ext） */
export function replySnapshotText(content: MessageContent): string {
  switch (content.kind) {
    case 'text':
      return [...content.text].slice(0, REPLY_TEXT_MAX).join('');
    case 'image':
    case 'album':
      return '[图片]';
    case 'voice':
      return '[语音]';
    case 'contact':
      return `[名片] ${content.name}`.trim();
    case 'video':
      return '[媒体]';
    case 'file':
      return '[文件]';
    default:
      return content.label;
  }
}

/**
 * 被引用消息 → 快照正文 envelope（21ah-2）：按 content 回写最小 envelope，
 * 接收端 parseMessageContent 可解回原样气泡。用既有的 build*Envelope 单点，
 * 不另起一套字段名；clock 一律不带——快照不是原消息，不该再被当阅后即焚计时。
 * 文本/占位返回 0（文本快照本身就够，别白占 ext 体积）。
 */
export function replySnapshotEnvelope(content: MessageContent): {
  msgType: number;
  srcMsg: string;
} {
  switch (content.kind) {
    case 'image':
      return {
        msgType: MsgType.Image,
        srcMsg: content.stickerPayload
          ? buildStickerEnvelope(
              {
                packId: content.stickerPackId,
                itemId: 0,
                path: content.url,
                width: content.width,
                height: content.height,
              },
              0,
            )
          : buildImageEnvelope(content.url, content.width, content.height),
      };
    // 相册整册没有单条 envelope（时间线合并出来的形态）：取首张当快照
    case 'album': {
      const first = content.images[0];
      return first === undefined
        ? { msgType: 0, srcMsg: '' }
        : {
            msgType: MsgType.Image,
            srcMsg: buildImageEnvelope(first.url, first.width, first.height),
          };
    }
    case 'voice':
      return {
        msgType: MsgType.Voice,
        srcMsg: buildVoiceEnvelope(content.url, content.durationMs, content.size, content.codec),
      };
    case 'video':
      return {
        msgType: MsgType.Video,
        srcMsg: buildFileEnvelope(content.url, content.fileName, content.size, content.mime),
      };
    case 'file':
      return {
        msgType: MsgType.File,
        srcMsg: buildFileEnvelope(content.url, content.fileName, content.size, content.mime),
      };
    case 'contact':
      return {
        msgType: MsgType.Card,
        srcMsg: buildContactCardEnvelope(
          content.userId,
          content.name,
          content.avatar,
          content.username,
        ),
      };
    default:
      return { msgType: 0, srcMsg: '' };
  }
}

/**
 * 菜单「回复」→ 快照引用信息（21ah-2 单点，调用方=ChatPage.onReply）。
 *
 * **阅后即焚 / 端到端加密 / 解密失败的消息不带正文快照**：ext 是明文存转的，
 * 把焚毁型或密文消息的正文塞进去等于绕过这两套机制（焚了还留一份、加密白加）。
 * 这三类只留文本占位，跳不过去时弹窗显那行字。
 */
export function replySnapshotRef(src: {
  readonly id: number;
  readonly name: string;
  readonly content: MessageContent;
  readonly secret: boolean;
  readonly encrypted: boolean;
  readonly decryptFailed: boolean;
}): ReplyRef {
  const text = replySnapshotText(src.content);
  const plain = !src.secret && !src.encrypted && !src.decryptFailed;
  const env = plain ? replySnapshotEnvelope(src.content) : { msgType: 0, srcMsg: '' };
  return { id: src.id, name: src.name, text, msgType: env.msgType, srcMsg: env.srcMsg };
}
